/**
 * «Envíalo por email» (27-09-2026): las reglas del envío de un informe o del
 * cuaderno por correo. COPIA DEL NAVEGADOR (la usa la tarjeta «Enviar por
 * email» del asistente). La del servidor es
 * supabase/functions/_shared/reportEmailRules.ts (edge `send-report-email` y
 * herramienta `propose_email`): el código es el MISMO salvo esta cabecera
 * (guardián src/__tests__/reportEmailRules.parity.test.ts). La tarjeta avisa
 * antes; quien decide es la edge.
 */

/** Como mucho 5 destinatarios por envío. */
export const MAX_DESTINATARIOS = 5;
/** Envíos por empresa en 24 h (se puede cambiar con REPORT_EMAIL_DAILY_LIMIT). */
export const LIMITE_DIARIO_POR_DEFECTO = 20;
/** Hasta aquí el fichero va además ADJUNTO; por encima, solo el botón «Descargar». */
export const ADJUNTO_MAX_BYTES = 5 * 1024 * 1024;
/** Un informe del chat que llega del navegador no puede pasar de esto. */
export const INFORME_MAX_BYTES = 10 * 1024 * 1024;
/** El enlace de descarga caduca a los 7 días. */
export const ENLACE_DIAS = 7;
export const ASUNTO_MAX = 150;
export const MENSAJE_MAX = 2000;
export const TITULO_MAX = 120;

/**
 * Las cuentas creadas por USUARIO llevan un correo de relleno de este dominio,
 * SIN buzón (M34 / T10-05): a una dirección así no se manda nunca nada real.
 */
export const DOMINIO_SIN_BUZON = "internal.ulture.app";

/**
 * Dominios a los que puede mandar una empresa INTERNA (Ulture QA, la demo, las
 * réplicas…): los tests nunca notifican a personas reales. `internal.ulture.app`
 * se SIMULA (no hay buzón); `resend.dev` son las direcciones de prueba del
 * proveedor (delivered@resend.dev: el correo sale de verdad y no lo lee nadie).
 * Se cambia con REPORT_EMAIL_TEST_DOMAINS.
 */
export const DOMINIOS_PRUEBA_POR_DEFECTO = [DOMINIO_SIN_BUZON, "resend.dev"];

export type FormatoEnvio = "pdf" | "xlsx";
export type OrigenEnvio = "report" | "cuaderno";

/** Los códigos de error que la edge devuelve y la tarjeta traduce (`ipmAssistant.emailCard.error.*`). */
export const CODIGOS_ERROR_ENVIO = [
  "EMAIL_NO_AUTENTICADO",
  "EMAIL_SIN_PERMISO",
  "EMAIL_DESTINO_INVALIDO",
  "EMAIL_DEMASIADOS_DESTINOS",
  "EMAIL_SIN_DESTINOS",
  "EMAIL_DESTINO_NO_PERMITIDO_EN_PRUEBAS",
  "EMAIL_DESTINO_SIN_BUZON",
  "EMAIL_LIMITE_DIARIO",
  "EMAIL_FICHERO_NO_ENCONTRADO",
  "EMAIL_FICHERO_INVALIDO",
  "EMAIL_FICHERO_GRANDE",
  "EMAIL_CUADERNO_APAGADO",
  "EMAIL_EN_CURSO",
  "EMAIL_PROVEEDOR",
  "EMAIL_ALMACEN",
  "EMAIL_PETICION_INVALIDA",
] as const;
export type CodigoErrorEnvio = (typeof CODIGOS_ERROR_ENVIO)[number];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const esUuid = (v: unknown): v is string => typeof v === "string" && UUID_RE.test(v);

/*
 * Una dirección normal y corriente: parte local de caracteres «seguros» (sin
 * comillas ni espacios), una arroba, un dominio con al menos un punto y un TLD de
 * letras. Deja fuera lo raro de la RFC a propósito: aquí solo se escribe a
 * personas, y lo raro es casi siempre una errata o una inyección de cabeceras.
 */
const EMAIL_RE = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,24}$/;

/** ¿Es una dirección válida? (ya en minúsculas y sin espacios). */
export function emailValido(email: string): boolean {
  if (email.length > 254) return false;
  if (!EMAIL_RE.test(email)) return false;
  const [local] = email.split("@");
  return local.length <= 64 && !local.startsWith(".") && !local.endsWith(".") && !local.includes("..");
}

export const dominioDe = (email: string): string => email.slice(email.lastIndexOf("@") + 1).toLowerCase();

export interface Destinatarios {
  /** Válidas, en minúsculas, sin repetir, en el orden en que se dijeron. */
  validos: string[];
  /** Lo que no es una dirección (tal cual, recortado). */
  invalidos: string[];
  /** Hay más de MAX_DESTINATARIOS válidas. */
  demasiados: boolean;
}

/**
 * Los destinatarios, de una lista o de un texto («a@b.es, c@d.com; e@f.org»).
 * Quita «mailto:», los <> de «Nombre <a@b.es>» y los espacios; no adivina nada.
 */
export function parseDestinatarios(raw: unknown): Destinatarios {
  const trozos: string[] = [];
  const meter = (v: unknown) => {
    if (typeof v !== "string") return;
    for (const t of v.split(/[,;\s]+/)) if (t.trim()) trozos.push(t.trim());
  };
  if (Array.isArray(raw)) raw.slice(0, 50).forEach(meter);
  else meter(raw);
  const validos: string[] = [];
  const invalidos: string[] = [];
  for (const t0 of trozos.slice(0, 50)) {
    const t = t0.replace(/^mailto:/i, "").replace(/^<|>$/g, "").toLowerCase();
    if (!t.includes("@")) {
      // «Nombre» suelto de «Nombre <a@b.es>»: no es una dirección, pero tampoco un error.
      if (/^[\p{L}"'.-]+$/u.test(t0) && trozos.some((x) => x.startsWith("<"))) continue;
      invalidos.push(t0.slice(0, 80));
      continue;
    }
    if (!emailValido(t)) {
      invalidos.push(t0.slice(0, 80));
      continue;
    }
    if (!validos.includes(t)) validos.push(t);
  }
  return { validos, invalidos, demasiados: validos.length > MAX_DESTINATARIOS };
}

export type ModoEnvio = "real" | "simulado";

export type DecisionEnvio =
  | { ok: true; modo: ModoEnvio }
  | { ok: false; error: "EMAIL_DESTINO_NO_PERMITIDO_EN_PRUEBAS" | "EMAIL_DESTINO_SIN_BUZON"; destinos: string[] };

/**
 * ¿Se puede mandar, y de verdad o simulado?
 *  - Empresa INTERNA (companies.is_internal): SOLO a los dominios de prueba; si
 *    alguno es el de relleno (sin buzón), el envío entero se simula.
 *  - Empresa cliente: a una dirección de relleno (sin buzón) no; al resto, sí.
 *  - `simular` (petición en ensayo o REPORT_EMAIL_MODE=simulate): todo simulado,
 *    con todas las comprobaciones de antes.
 */
export function decidirEnvio(o: {
  esInterna: boolean;
  destinatarios: string[];
  dominiosPrueba: string[];
  simular: boolean;
}): DecisionEnvio {
  const prueba = new Set(o.dominiosPrueba.map((d) => d.trim().toLowerCase().replace(/^@/, "")).filter(Boolean));
  if (o.esInterna) {
    const fuera = o.destinatarios.filter((e) => !prueba.has(dominioDe(e)));
    if (fuera.length) return { ok: false, error: "EMAIL_DESTINO_NO_PERMITIDO_EN_PRUEBAS", destinos: fuera };
    const sinBuzon = o.destinatarios.some((e) => dominioDe(e) === DOMINIO_SIN_BUZON);
    return { ok: true, modo: o.simular || sinBuzon ? "simulado" : "real" };
  }
  const sinBuzon = o.destinatarios.filter((e) => dominioDe(e) === DOMINIO_SIN_BUZON);
  if (sinBuzon.length) return { ok: false, error: "EMAIL_DESTINO_SIN_BUZON", destinos: sinBuzon };
  return { ok: true, modo: o.simular ? "simulado" : "real" };
}

/** Lista de dominios de una variable de entorno («a.com, @b.org»); vacía o ausente → la de por defecto. */
export function dominiosDeEntorno(raw: string | null | undefined): string[] {
  const l = String(raw ?? "")
    .split(/[,;\s]+/)
    .map((d) => d.trim().toLowerCase().replace(/^@/, ""))
    .filter(Boolean);
  return l.length ? l : [...DOMINIOS_PRUEBA_POR_DEFECTO];
}

// Caracteres de control (C0, DEL y C1) y los separadores de línea/párrafo de Unicode.
// eslint-disable-next-line no-control-regex -- quitar el control es justo lo que se quiere
const CONTROL_RE = /[\u0000-\u001f\u007f-\u009f\u2028\u2029]/g;

/** Asunto: una línea (nada de saltos: inyección de cabeceras), sin control, con tope. */
export function limpiarAsunto(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw.replace(CONTROL_RE, " ").replace(/\s+/g, " ").trim().slice(0, ASUNTO_MAX);
}

/** Mensaje: saltos de línea sí (como mucho uno en blanco seguido), sin control, con tope. */
export function limpiarMensaje(raw: unknown): string {
  if (typeof raw !== "string") return "";
  return raw
    .replace(/\r\n?/g, "\n")
    // eslint-disable-next-line no-control-regex -- ídem, dejando el salto de línea
    .replace(/[\u0000-\u0009\u000b-\u001f\u007f-\u009f\u2028\u2029]/g, " ")
    .split("\n")
    .map((l) => l.replace(/[ \t]+/g, " ").trimEnd())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
    .slice(0, MENSAJE_MAX);
}

/** Título del documento (una línea, sin control, con tope). */
export function limpiarTitulo(raw: unknown): string {
  return limpiarAsunto(raw).slice(0, TITULO_MAX);
}

/** Nombre de fichero seguro para el adjunto y la descarga: letras, números, «-», «_» y «.». */
export function nombreFichero(base: string, formato: FormatoEnvio): string {
  const sinExt = String(base ?? "").replace(/\.(pdf|xlsx)$/i, "");
  const limpio = sinExt
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "")
    .slice(0, 80);
  return `${limpio || "documento"}.${formato}`;
}

/** ¿Los bytes son de verdad un PDF o un .xlsx? (cabecera «%PDF-» o ZIP «PK\x03\x04»). */
export function esFicheroDeFormato(bytes: Uint8Array, formato: FormatoEnvio): boolean {
  if (formato === "pdf") return bytes.length > 5 && String.fromCharCode(...bytes.slice(0, 5)) === "%PDF-";
  return bytes.length > 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

export const MIME_ENVIO: Record<FormatoEnvio, string> = {
  pdf: "application/pdf",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};

/**
 * El interruptor del cuaderno (`platform_settings.cuaderno_campo`), la MISMA regla
 * que `ipm-ai-assistant/mode.ts#cuadernoEnabledFor` y el cliente: sin fila,
 * apagado; lista vacía, ninguna empresa; `"all": true` (el booleano), todas,
 * también las futuras (petición de Pablo, 05-10-2026). Un cuaderno solo se
 * envía con el cuaderno encendido para la empresa (CU5).
 */
export function cuadernoEncendido(setting: unknown, companyId: string | null | undefined): boolean {
  if (!setting || typeof setting !== "object" || !companyId) return false;
  const s = setting as { enabled?: unknown; all?: unknown; companies?: unknown };
  if (s.enabled !== true) return false;
  if (s.all === true) return true;
  return Array.isArray(s.companies) && s.companies.includes(companyId);
}

/** El tamaño para el correo («1,2 MB», «640 KB»), con coma o punto según el idioma. */
export function tamanoLegible(bytes: number, lang: string): string {
  const coma = !["en", "zh"].includes(lang);
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  const mb = (bytes / (1024 * 1024)).toFixed(1);
  return `${coma ? mb.replace(".", ",") : mb} MB`;
}
